import * as THREE from 'three'

export const DitherAsciiShader = {
    name: 'DitherAsciiShader',

    uniforms: {
        tDiffuse: { value: null },
        uResolution: { value: new THREE.Vector2(1, 1) },
        uTime: { value: 0 },
        uNoiseScale: { value: 3.0 },
        uNoiseSpeed: { value: 0.3 },
        uNoiseThreshold: { value: 0.5 },
        uNoiseSoftness: { value: 0.1 },
        uPixelSize: { value: 6.0 },
        uMode: { value: 0.0 },
        uToonEnabled: { value: 1.0 },
        uToonLevels: { value: 4.0 },
        uToonColor1: { value: new THREE.Vector3(0.102, 0.102, 0.180) },
        uToonColor2: { value: new THREE.Vector3(0.086, 0.129, 0.243) },
        uToonColor3: { value: new THREE.Vector3(0.059, 0.204, 0.376) },
        uToonColor4: { value: new THREE.Vector3(0.914, 0.271, 0.376) },
        uShowPixelUnderneath: { value: 1.0 },
        uCharBrightnessDelta: { value: 0.3 },
        uCharDarker: { value: 1.0 },
        tDisplacement: { value: null },
        uDisplacementStrength: { value: 0.4 },
        uExposure: { value: 1.0 },
        tModelMask: { value: null },
        uMaskOnly: { value: 1.0 },
        tWorldPos: { value: null },
        uUseWorldPos: { value: 0.0 },
    },

    vertexShader: /* glsl */`
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,

    fragmentShader: /* glsl */`
        uniform sampler2D tDiffuse;
        uniform vec2 uResolution;
        uniform float uTime;
        uniform float uNoiseScale;
        uniform float uNoiseSpeed;
        uniform float uNoiseThreshold;
        uniform float uNoiseSoftness;
        uniform float uPixelSize;
        uniform float uMode;
        uniform float uToonEnabled;
        uniform float uToonLevels;
        uniform vec3 uToonColor1;
        uniform vec3 uToonColor2;
        uniform vec3 uToonColor3;
        uniform vec3 uToonColor4;
        uniform float uShowPixelUnderneath;
        uniform float uCharBrightnessDelta;
        uniform float uCharDarker;
        uniform sampler2D tDisplacement;
        uniform float uDisplacementStrength;
        uniform float uExposure;
        uniform sampler2D tModelMask;
        uniform float uMaskOnly;
        uniform sampler2D tWorldPos;
        uniform float uUseWorldPos;

        varying vec2 vUv;

        // ACES filmic tone mapping
        vec3 ACESFilm(vec3 x) {
            float a = 2.51;
            float b = 0.03;
            float c = 2.43;
            float d = 0.59;
            float e = 0.14;
            return clamp((x*(a*x+b))/(x*(c*x+d)+e), 0.0, 1.0);
        }

        vec3 applyExposureAndTonemap(vec3 color) {
            color *= uExposure;
            color = ACESFilm(color);
            // Linear to sRGB
            color = pow(color, vec3(1.0 / 2.2));
            return color;
        }

        // ============================================================
        // Simplex noise 3D (single octave, optimized)
        // ============================================================
        vec3 mod289v3(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289v4(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289v4(((x * 34.0) + 10.0) * x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

        float snoise(vec3 v) {
            const vec2 C = vec2(1.0/6.0, 1.0/3.0);
            vec3 i = floor(v + dot(v, C.yyy));
            vec3 x0 = v - i + dot(i, C.xxx);
            vec3 g = step(x0.yzx, x0.xyz);
            vec3 l = 1.0 - g;
            vec3 i1 = min(g, l.zxy);
            vec3 i2 = max(g, l.zxy);
            vec3 x1 = x0 - i1 + C.xxx;
            vec3 x2 = x0 - i2 + C.yyy;
            vec3 x3 = x0 - 0.5;
            i = mod289v3(i);
            vec4 p = permute(permute(permute(
                i.z + vec4(0.0, i1.z, i2.z, 1.0))
                + i.y + vec4(0.0, i1.y, i2.y, 1.0))
                + i.x + vec4(0.0, i1.x, i2.x, 1.0));
            vec3 ns = 0.142857142857 * vec4(0.0, 0.5, 1.0, 2.0).wyz - vec4(0.0, 0.5, 1.0, 2.0).xzx;
            vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
            vec4 x_ = floor(j * ns.z);
            vec4 y_ = floor(j - 7.0 * x_);
            vec4 x4 = x_ * ns.x + ns.yyyy;
            vec4 y4 = y_ * ns.x + ns.yyyy;
            vec4 h = 1.0 - abs(x4) - abs(y4);
            vec4 b0 = vec4(x4.xy, y4.xy);
            vec4 b1 = vec4(x4.zw, y4.zw);
            vec4 s0 = floor(b0) * 2.0 + 1.0;
            vec4 s1 = floor(b1) * 2.0 + 1.0;
            vec4 sh = -step(h, vec4(0.0));
            vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
            vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
            vec3 p0 = vec3(a0.xy, h.x);
            vec3 p1 = vec3(a0.zw, h.y);
            vec3 p2 = vec3(a1.xy, h.z);
            vec3 p3 = vec3(a1.zw, h.w);
            vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
            p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
            vec4 m = max(0.5 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
            m = m * m;
            return 105.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
        }

        // ============================================================
        // Bayer 4x4 dithering (branchless)
        // ============================================================
        float bayerDither(vec2 pixelCoord) {
            vec2 p = mod(pixelCoord, 4.0);
            float x = floor(p.x);
            float y = floor(p.y);
            return mod(x + y * 2.0, 4.0) * 4.0 + mod(x * 2.0 + y, 4.0) / 16.0;
        }

        // ============================================================
        // ASCII: varied patterns per luminosity band (branchless)
        // Each band uses a distinct character shape for visual variety
        // ============================================================
        float asciiChar(vec2 uv, float lum) {
            vec2 c = uv - 0.5;
            float ax = abs(c.x);
            float ay = abs(c.y);
            float level = floor(lum * 11.99);

            float fill = 0.0;

            // Level 1: single dot (period)
            fill = max(fill, step(length(c - vec2(0.0, 0.15)), 0.07) * step(1.0, level));

            // Level 2: colon (two dots vertical)
            float colon = max(
                step(length(c - vec2(0.0, 0.12)), 0.06),
                step(length(c + vec2(0.0, 0.12)), 0.06)
            );
            fill = max(fill, colon * step(2.0, level));

            // Level 3: dash (horizontal bar)
            fill = max(fill, step(ay, 0.05) * step(ax, 0.2) * step(3.0, level));

            // Level 4: tilde (wavy line)
            float wave = step(abs(c.y - sin(c.x * 12.0) * 0.08), 0.05) * step(ax, 0.25);
            fill = max(fill, wave * step(4.0, level));

            // Level 5: plus sign
            float plus = max(
                step(ay, 0.05) * step(ax, 0.2),
                step(ax, 0.05) * step(ay, 0.2)
            );
            fill = max(fill, plus * step(5.0, level));

            // Level 6: asterisk (star shape)
            float star = max(
                max(step(ay, 0.04) * step(ax, 0.22),
                    step(ax, 0.04) * step(ay, 0.22)),
                step(abs(ax - ay), 0.04) * step(max(ax, ay), 0.2)
            );
            fill = max(fill, star * step(6.0, level));

            // Level 7: hash (grid)
            float h1 = step(abs(c.x - 0.1), 0.035);
            float h2 = step(abs(c.x + 0.1), 0.035);
            float h3 = step(abs(c.y - 0.1), 0.035);
            float h4 = step(abs(c.y + 0.1), 0.035);
            float hash = max(max(h1, h2), max(h3, h4)) * step(max(ax, ay), 0.3);
            fill = max(fill, hash * step(7.0, level));

            // Level 8: percent (two circles + diagonal)
            float pctTL = step(length(c - vec2(-0.12, -0.15)), 0.08);
            float pctBR = step(length(c - vec2(0.12, 0.15)), 0.08);
            float pctSlash = step(abs(c.x + c.y), 0.04) * step(max(ax, ay), 0.28);
            fill = max(fill, max(max(pctTL, pctBR), pctSlash) * step(8.0, level));

            // Level 9: ampersand shape (ring + tail)
            float ring = step(abs(length(c - vec2(-0.02, -0.05)) - 0.13), 0.04);
            float tail = step(abs(c.y - c.x * 0.7 - 0.1), 0.04) * step(c.x, 0.2) * step(-0.05, c.x);
            fill = max(fill, max(ring, tail) * step(9.0, level));

            // Level 10: at sign (circle + inner)
            float atOuter = step(abs(length(c) - 0.2), 0.04);
            float atInner = step(length(c - vec2(0.06, 0.0)), 0.1);
            float atTail = step(ay, 0.04) * step(c.x, 0.3) * step(0.15, c.x);
            fill = max(fill, max(max(atOuter, atInner), atTail) * step(10.0, level));

            // Level 11: dense block
            fill = max(fill, step(ax, 0.32) * step(ay, 0.38) * step(11.0, level));

            return fill;
        }

        // ============================================================
        // Toon quantization - returns color AND band index (0-3)
        // ============================================================
        vec3 toonQuantize(vec3 color, float lum, out float bandLum) {
            float levels = uToonLevels;
            float quantized = floor(lum * levels) / levels;
            float s = 1.0 / levels;

            vec3 c = uToonColor1;
            c = mix(c, uToonColor2, step(s, quantized));
            c = mix(c, uToonColor3, step(s * 2.0, quantized));
            c = mix(c, uToonColor4, step(s * 3.0, quantized));

            // Return luminosity of the toon color for char selection
            bandLum = dot(c, vec3(0.299, 0.587, 0.114));
            return c;
        }

        void main() {
            vec2 uv = vUv;
            vec4 originalRaw = texture2D(tDiffuse, uv);
            vec4 original = vec4(applyExposureAndTonemap(originalRaw.rgb), 1.0);

            // Sample the full-res MSAA model mask once: it's the authoritative
            // silhouette and is also used to gate the half-res worldPos read
            // (which would otherwise bleed outside the model edge).
            float modelMask = texture2D(tModelMask, uv).r;

            // --- Mouse trail mask (cheap, single texture sample) ---
            float trailMask = texture2D(tDisplacement, uv).r * uDisplacementStrength;

            // Early out for clearly-background pixels — skip the expensive
            // 3D simplex noise + downstream work entirely. Two cases:
            //   1. screen mode + maskOnly: bg pixels are passed through
            //   2. material mode (uUseWorldPos): bg pixels are discarded by
            //      the composite shader anyway, so output is irrelevant
            // Trail can extend past the model, so only skip when trail is low.
            if ((uMaskOnly > 0.5 || uUseWorldPos > 0.5) && modelMask < 0.01 && trailMask < 0.01) {
                gl_FragColor = original;
                return;
            }

            // --- Noise mask ---
            vec3 noiseInput = vec3(uv * uNoiseScale, uTime * uNoiseSpeed);
            if (uUseWorldPos > 0.5) {
                // World-space noise: sticks to model surface.
                // Gate by the full-res MSAA mask with a high threshold so the
                // half-res worldPos texels don't bleed past the silhouette.
                vec3 wpSample = texture2D(tWorldPos, uv).rgb;
                float hasWP = step(0.95, modelMask);
                vec3 worldPos = (wpSample - 0.5) * 10.0;
                noiseInput = mix(
                    noiseInput,
                    vec3(worldPos.xz * uNoiseScale, uTime * uNoiseSpeed + worldPos.y * 0.5),
                    hasWP
                );
            }
            float noise = snoise(noiseInput) * 0.5 + 0.5;
            noise = smoothstep(0.0, 1.0, noise);
            noise = smoothstep(0.0, 1.0, noise);
            float noiseMask = smoothstep(uNoiseThreshold - uNoiseSoftness, uNoiseThreshold + uNoiseSoftness, noise);

            // Combine noise + trail
            float mask = min(max(noiseMask, trailMask), 1.0);

            // Apply model mask: only affect the 3D model, not background
            mask *= mix(1.0, modelMask, uMaskOnly);

            // Final early out (handles cases where noise+trail+mask cancel out)
            if (mask < 0.01) {
                gl_FragColor = original;
                return;
            }

            // --- Pixelation ---
            vec2 pixelCount = uResolution / uPixelSize;
            vec2 pixelatedUv = floor(uv * pixelCount) / pixelCount;
            vec3 col = applyExposureAndTonemap(texture2D(tDiffuse, pixelatedUv).rgb);

            // Original luminosity
            float lum = dot(col, vec3(0.299, 0.587, 0.114));

            // --- Toon (before dither/ASCII) ---
            vec3 baseCol = col;
            float charLum = lum; // luminosity used for char/dither pattern selection
            if (uToonEnabled > 0.5) {
                float bandLum;
                baseCol = toonQuantize(col, lum, bandLum);
                // Use toon band luminosity for pattern selection
                // This gives each toon band a distinct character
                charLum = bandLum;
            }

            // --- Char color with contrast ---
            float delta = uCharBrightnessDelta;
            vec3 charCol;
            if (uToonEnabled > 0.5) {
                charCol = uCharDarker > 0.5
                    ? baseCol * max(1.0 - delta * 3.0, 0.0)
                    : baseCol + (vec3(1.0) - baseCol) * delta * 3.0;
            } else {
                charCol = uCharDarker > 0.5
                    ? baseCol * (1.0 - delta)
                    : baseCol * (1.0 + delta);
            }
            charCol = clamp(charCol, 0.0, 1.0);

            // --- Dithering or ASCII ---
            vec3 result;
            vec2 cellUv = fract(uv * pixelCount);

            if (uMode < 0.5) {
                float dithered = step(bayerDither(floor(uv * uResolution)), charLum);
                result = uShowPixelUnderneath > 0.5
                    ? mix(baseCol, charCol, dithered)
                    : vec3(dithered);
            } else {
                float charVal = asciiChar(cellUv, charLum);
                result = uShowPixelUnderneath > 0.5
                    ? mix(baseCol, charCol, charVal)
                    : vec3(charVal);
            }

            gl_FragColor = vec4(mix(original.rgb, result, mask), 1.0);
        }
    `,
}
